import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { uniqBy } from "lodash";

// Material UI
import { useTheme } from "@mui/styles";
import { Grid, MenuItem, Button, TextField } from "@mui/material";

// Custom Components
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import SearchBar from "../../components/SearchBar/SearchBar";

// Hooks
import useScopedTranslation from "../../hooks/useScopedTranslation";
import { useLocations } from "../../hooks/apiHooks/locations";
import { usePositions } from "../../hooks/apiHooks/positions";

//Constants
import { states } from "../../constants";
import DatePicker from "../../components/DatePicker/DatePicker";

const locationsToStates = locations => {
  return uniqBy(locations, "state")
    .map(location => states.find(state => state.value === location.state))
    .filter(state => !!state)
    .map(state => ({ name: state.label, id: state.value }));
};

const ManageGroupMembersFilters = ({ onFilter, group }) => {
  const [term, setTerm] = useState("");
  const [location, setLocation] = useState("");
  const [position, setPosition] = useState("");
  const [state, setState] = useState("");
  const [hiredSince, setHiredSince] = useState(null);
  const [hiredBefore, setHiredBefore] = useState(null);

  const [locations, setLocations] = useState([]);
  const [states, setStates] = useState([]);
  const [positions, setPositions] = useState([]);

  const { getLocations } = useLocations();
  const { getPositions } = usePositions();

  const t = useScopedTranslation("employeesFilters");
  const theme = useTheme();

  useEffect(() => {
    getLocations({
      filters: { active: true, divisionId: group?.customer?.id },
      fields: { location: ["name", "state"] },
      sort: "name"
    }).then(response => {
      const locations = response.data;
      setLocations(locations);
      setStates(locationsToStates(locations));
    });
    getPositions({
      filters: { active: true, divisionId: group?.customer?.id },
      fields: { position: ["name"] },
      sort: "name"
    }).then(response => {
      const positions = response.data;
      setPositions(positions);
    });
  }, []);

  const setFilters = () => {
    onFilter({
      term,
      location,
      position,
      state,
      hiredSince,
      hiredBefore
    });
  };

  const clearFilters = () => {
    onFilter({});
    setTerm("");
    setLocation("");
    setPosition("");
    setState("");
    setHiredSince(null);
    setHiredBefore(null);
  };

  return (
    <Grid container spacing={2} sx={{ pb: 2, alignItems: "center" }}>
      <Grid item xs={12} md={4}>
        <SearchBar
          onSearchSubmit={value => {
            setTerm(value);
            onFilter({ term: value });
          }}
          filled
          fullWidth
          searchIconPosition="end"
          background={theme.palette.inputBackground}
          placeholder={t("fullName")}
          value={term}
          onSearchInput={value => setTerm(value)}
          controlled
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          select
          fullWidth
          name="location"
          value={location}
          onChange={e => setLocation(e.target.value)}
          variant="filled"
          label={t("location")}
          InputProps={{
            disableUnderline: true,
            onKeyDown: ({ key }) => key === "Enter" && setFilters()
          }}
        >
          <MenuItem value="">{t("location")}</MenuItem>
          {locations.map(location => (
            <MenuItem value={location.id} key={location.id}>
              {location.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          select
          fullWidth
          name="state"
          value={state}
          variant="filled"
          onChange={e => setState(e.target.value)}
          label={t("state")}
          InputProps={{
            disableUnderline: true,
            onKeyDown: ({ key }) => key === "Enter" && setFilters()
          }}
        >
          <MenuItem value="">{t("state")}</MenuItem>
          {states.map(state => (
            <MenuItem value={state.id} key={state.id}>
              {state.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          select
          fullWidth
          name="position"
          value={position}
          variant="filled"
          onChange={e => setPosition(e.target.value)}
          label={t("position")}
          InputProps={{
            disableUnderline: true,
            onKeyDown: ({ key }) => key === "Enter" && setFilters()
          }}
        >
          <MenuItem value="">{t("position")}</MenuItem>
          {positions.map(position => (
            <MenuItem value={position.id} key={position.id}>
              {position.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} md={2}>
        <DatePicker
          fullWidth
          name="hiredSince"
          label={t("hiredSince")}
          onChange={value => {
            setHiredSince(value);
          }}
          format="yyyy-MM-dd"
          value={hiredSince}
          animateYearScrolling={false}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <DatePicker
          fullWidth
          name="hiredBefore"
          label={t("hiredBefore")}
          onChange={value => {
            setHiredBefore(value);
          }}
          format="yyyy-MM-dd"
          value={hiredBefore}
          animateYearScrolling={false}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <ButtonGroup>
          <Button
            color="primary"
            onClick={clearFilters}
            data-qa="manage-group-members-clear-filters-btn"
          >
            {t("clearAll")}
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={setFilters}
            data-qa="manage-group-members-submit-filters-btn"
          >
            {t("submit")}
          </Button>
        </ButtonGroup>
      </Grid>
    </Grid>
  );
};

ManageGroupMembersFilters.propTypes = {
  group: PropTypes.shape({
    customer: PropTypes.shape({
      id: PropTypes.string
    })
  }),
  onFilter: PropTypes.func
};

export default ManageGroupMembersFilters;
